// src/pages/ProductPage.jsx

import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchFilters, resetFilters } from "../redux/actions/filterActions";
import {
  getAllProducts,
  getFilteredProducts,
} from "../redux/actions/productFilterActions";
import { useSearchParams } from "react-router-dom";
import { BsSliders } from "react-icons/bs";
import { IoMdSearch } from "react-icons/io";
import Products from "../components/category/Products";
import * as filter from "../components/filter";
import { hasActiveFilters } from "../Services/utils";

const FilterSkeleton = () => (
  <div>
    <div className="animate-pulse bg-gray-200 h-8 w-3/4 mb-4 rounded"></div>
    <div className="animate-pulse bg-gray-200 h-6 w-1/2 mb-3 rounded"></div>
    <div className="animate-pulse bg-gray-200 h-6 w-2/3 mb-3 rounded"></div>
    <div className="animate-pulse bg-gray-200 h-6 w-1/3 mb-3 rounded"></div>
    <div className="animate-pulse bg-gray-200 h-8 w-1/2 mb-4 rounded"></div>
  </div>
);

export default function ProductPage() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [localSearch, setLocalSearch] = useState("");
  const searchTimeout = useRef(null);
  const filterRef = useRef(null);
  const [filterHeight, setFilterHeight] = useState();

  /* ───────── URL STATE ───────── */
  const page = Number(searchParams.get("page")) || 1;

  const categories = searchParams.get("category")
    ? [searchParams.get("category")]
    : [];

  const subCategory = searchParams.get("subCategory") || "";
  const searchTerm = searchParams.get("searchTerm") || "";

  /* ───────── REDUX STATE ───────── */
  const { loading: optionsLoading } = useSelector(
    (state) => state.filterOptions
  );

  const chosenFilters = useSelector((state) => state.filters);
  const filtersActive = hasActiveFilters(chosenFilters);

  const {
    products,
    pagination,
    loading: productsLoading,
    error: productsError,
  } = useSelector((state) => state.productFilter);

  /* ───────── RESET FILTERS ON MOUNT ───────── */
  useEffect(() => {
    dispatch(resetFilters());
  }, [dispatch]);

  useEffect(() => {
    if (searchTerm) {
      dispatch(
        getFilteredProducts({ filters: { search: searchTerm }, page: 1 })
      );
    }
  }, []);

  /* ───────── FETCH FILTER OPTIONS ───────── */
  useEffect(() => {
    if (!searchTerm) {
      dispatch(
        fetchFilters({
          category_id: searchParams.get("catId"),
          subcategory_id: searchParams.get("subCatId"),
        })
      );
    }
  }, [dispatch, searchParams]);

  /* ───────── FILTER MODE (RESET PAGE) ───────── */
  useEffect(() => {
    if (!filtersActive) return;

    const params = Object.fromEntries(searchParams.entries());
    params.page = 1;
    setSearchParams(params);

    dispatch(getFilteredProducts({ filters: chosenFilters, page: 1 }));
  }, [filtersActive, dispatch, chosenFilters]);

  /* ───────── ALL PRODUCTS MODE ───────── */
  useEffect(() => {
    if (filtersActive || searchTerm) return;

    dispatch(
      getAllProducts({
        categories,
        subCategory,
        page,
      })
    );
  }, [filtersActive, page, categories.join(","), subCategory, dispatch]);

  /* ───────── SEARCH HANDLERS ───────── */
  const dispatchFilteredSearch = (value) => {
    const params = Object.fromEntries(searchParams.entries());
    params.page = 1;
    setSearchParams(params);
    dispatch(
      getFilteredProducts({
        filters: { ...chosenFilters, search: value },
        page: 1,
      })
    );
  };

  const onSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearch(value);

    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      dispatchFilteredSearch(value);
    }, 400);
  };

  const onSearchClick = () => {
    dispatchFilteredSearch(localSearch);
  };

  const onSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      clearTimeout(searchTimeout.current);
      dispatchFilteredSearch(localSearch);
    }
  };

  /* ───────── RESET FILTERS ───────── */
  const onReset = () => {
    setLocalSearch("");
    dispatch(resetFilters());
    dispatch(
      fetchFilters({
        category_id: searchParams.get("catId"),
        subcategory_id: searchParams.get("subCatId"),
      })
    );
    const params = Object.fromEntries(searchParams.entries());
    params.page = 1;
    setSearchParams(params);
  };

  /* ───────── PAGINATION ───────── */
  const onNext = () => {
    const params = Object.fromEntries(searchParams.entries());
    params.page = page + 1;
    setSearchParams(params);

    // if (filtersActive) {
    //   dispatch(
    //     getFilteredProducts({
    //       ...chosenFilters,
    //       page: searchParams.get("page"),
    //     })
    //   );
    // } else {
    //   dispatch(
    //     getAllProducts({
    //       categories,
    //       subCategory,
    //       page: searchParams.get("page"),
    //     })
    //   );
    // }
  };

  const onPrevious = () => {
    if (page <= 1) return;

    const params = Object.fromEntries(searchParams.entries());
    params.page = page - 1;
    setSearchParams(params);

    // if (filtersActive) {
    //   dispatch(
    //     getFilteredProducts({
    //       ...chosenFilters,
    //       page: searchParams.get("page"),
    //     })
    //   );
    // } else {
    //   dispatch(
    //     getAllProducts({
    //       categories,
    //       subCategory,
    //       page: searchParams.get("page"),
    //     })
    //   );
    // }
  };

  /* ───────── RESIZE OBSERVER ───────── */
  useEffect(() => {
    if (!filterRef.current) return;

    let lastHeight = 0;
    let rafId = null;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const newHeight = Math.round(entry.contentRect.height);

      // 🛑 Guard: only update if height actually changed
      if (newHeight !== lastHeight) {
        lastHeight = newHeight;

        if (rafId) cancelAnimationFrame(rafId);

        rafId = requestAnimationFrame(() => {
          setFilterHeight(newHeight);
        });
      }
    });

    observer.observe(filterRef.current);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, []);

  return (
    <section className="flex flex-col w-full lg:flex-row gap-5 p-4">
      {/* ───────── FILTER SIDEBAR ───────── */}
      <div
        ref={filterRef}
        className={`h-full w-full lg:w-1/4 p-2 ${
          searchParams.get("searchTerm") && "hidden"
        }`}
      >
        <div
          onClick={() => setIsFilterOpen((p) => !p)}
          className="flex items-center gap-4 text-xl cursor-pointer"
        >
          <BsSliders />
          <span>Filter</span>
        </div>

        <div className="bg-search-bg rounded-2xl p-2 mt-5 flex items-center">
          <input
            type="text"
            placeholder="Search Product"
            value={localSearch}
            onChange={onSearchChange}
            onKeyPress={onSearchKeyPress}
            className="bg-search-bg border-none outline-none w-full"
          />
          <span className="text-xl cursor-pointer" onClick={onSearchClick}>
            <IoMdSearch />
          </span>
        </div>

        {isFilterOpen &&
          (optionsLoading ? (
            <FilterSkeleton />
          ) : (
            <>
              <filter.PriceFilter />
              <filter.SizeFilter />
              <filter.ColorFilter showRadio={false} />
              <filter.TypesFilter />
              <filter.TagFilter />

              <button
                onClick={onReset}
                className="mt-4 w-full py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
              >
                Reset Filters
              </button>
            </>
          ))}
      </div>

      {/* ───────── PRODUCT GRID ───────── */}
      <div
        className={`w-full h-full p-2 flex flex-col gap-4 overflow-hidden ${
          searchParams.get("searchTerm") ? "" : "lg:w-3/4"
        }`}
        style={{ height: filterHeight ? `${filterHeight}px` : "auto" }}
      >
        <div className="border-b border-medium-theme-color shrink-0">
          <h2 className="text-2xl font-semibold">
            {subCategory ? subCategory.toUpperCase() : "All Products"}
            {localSearch && (
              <span className="text-base text-gray-600 ml-2">
                - Search: "{localSearch}"
              </span>
            )}
          </h2>
        </div>

        {productsLoading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="animate-pulse bg-gray-200 h-64 rounded-xl"
              />
            ))}
          </div>
        )}

        {productsError && (
          <p className="text-red-500 text-center">{productsError}</p>
        )}

        <div
          style={{ maxHeight: "100%" }}
          className={`flex-1 overflow-y-auto grid grid-cols-2 sm:grid-cols-2 gap-6 ${
            searchParams.get("searchTerm")
              ? "md:grid-cols-4 xl:grid-cols-5"
              : "md:grid-cols-3 xl:grid-cols-4"
          }`}
        >
          {!productsLoading && !productsError && (
            <Products products={products} pagination={pagination} />
          )}
        </div>

        {products.length > 0 && (
          <div className="bg-white flex flex-col justify-center items-center">
            <span>
              Showing {pagination?.page_size * (pagination?.page - 1) + 1} to{" "}
              {Math.min(
                pagination?.page_size * pagination?.page,
                pagination?.total_products
              )}{" "}
              of {pagination?.total_products} Results
            </span>

            <div className="flex gap-2 mt-2">
              <button
                onClick={onPrevious}
                disabled={pagination?.previous === null}
                className="px-4 py-2 border rounded disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={onNext}
                disabled={pagination?.next === null}
                className="px-4 py-2 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
